import {useCallback, useContext} from "react"
import {useParams, useSearchParams} from "react-router-dom"
import {EditProfile} from "../../components/Profile/EditProfile"
import {ProfileRepoListing} from "../../components/Profile/ProfileRepoListing"
import {BackendContext} from "../../context/BackendContext"
import {ProfileContext} from "../../context/Profile.context"
import {UIHelperContext} from "../../context/UIHelper.context"
import {useAvatar} from "../../hooks/useAvatar"
import {useFetchProfile} from "../../hooks/useFetchProfile"
import {useRepoList} from "../../hooks/useRepoList"
import {useUserID} from "../../hooks/useUserId"
import styles from "./index.module.scss"
import solanaImage from "../../assets/profile/solana_small.png"
import linkedInImage from "../../assets/profile/linkedin_small.png"
import twitterImage from "../../assets/profile/twitter_small.png"

export const Profile = () => {
  const {ipfs} = useContext(BackendContext)
  const {profile: myProfile,} = useContext(ProfileContext)
  const {setSnackMessage} = useContext(UIHelperContext)
  const {orbitId, profilePk} = useParams()
  const profileDBAddress = orbitId && profilePk ? `/orbitdb/${orbitId}/${profilePk}` : undefined
  const {profile, profileReplCnt} = useFetchProfile(profileDBAddress)
  const {userID} = useUserID(profile)
  const {repos} = useRepoList(profile)
  const {avatar} = useAvatar(profile, profileReplCnt)
  const [searchParams, setSearchParams] = useSearchParams()

  const onEditProfile = useCallback(() => {
    setSearchParams({editing: "true"})
  }, [])

  const onCopyPublicKey = useCallback(async () => {
    const publicKey = profile?.address.path
    if (!publicKey) {
      return
    }
    await navigator.clipboard.writeText(publicKey)
    setSnackMessage("Copied public key to clipboard")
  }, [setSnackMessage, profile])

  const onCopyUserID = useCallback(async () => {
    if (!userID) {
      return
    }
    await navigator.clipboard.writeText(userID)
    setSnackMessage("Copied user ID to clipboard")
  }, [userID])

  if (!profile || profileReplCnt === undefined || !ipfs || !myProfile || !userID) {
    return null
  }

  const isEditable = profile.address.path === myProfile.address.path
  const editing = searchParams.get("editing") === "true" && isEditable

  const publicKey = profile.address.path
  const truncPublicKey = publicKey.slice(0, 6) + "...." + publicKey.slice(-4)

  const truncUserId = userID.slice(0, 6) + "...." + userID.slice(-4)

  const userName = profile.get("name")
  const bio = profile.get("bio")
  const linkedIn = profile.get("linkedInURL")
  const twitter = profile.get("twitterURL")

  if (editing) {
    return (
      <div className={styles.MainContainer}>
        <EditProfile profile={profile} ipfs={ipfs} avatar={avatar}/>
      </div>
    )
  }

  return (
    <div className={styles.MainContainer}>
      <div className={styles.ViewProfileContainer}>
        <div className={styles.Header}>
          <img src={avatar} alt={"Profile picture"} className={styles.Avatar}/>
          <div className={styles.Names}>
            <div className={styles.DisplayName}>{userName}</div>
            <div className={styles.UserID} onClick={onCopyUserID}>
              User ID: {truncUserId}
            </div>
            <div className={styles.PublicKey} onClick={onCopyPublicKey}>
              <img src={solanaImage} alt={"Solana logo"} className={styles.SolanaLogo}/>
              <div>{truncPublicKey}</div>
            </div>
            <div className={styles.SocialMedia}>
              {
                linkedIn &&
                <a href={linkedIn} target="_blank">
                  <img src={linkedInImage} alt="Linkedin"/>
                </a>
              }
              {
                twitter &&
                <a href={twitter} target="_blank">
                  <img src={twitterImage} alt="Twitter"/>
                </a>
              }
            </div>
          </div>
          <div className={styles.Bio}>
            {bio}
          </div>

          {
            isEditable
              ? <button className={styles.EditButton} onClick={onEditProfile}>Edit</button>
              : <button className={styles.DonateButton}>Donate</button>
          }
        </div>

        <ProfileRepoListing repos={repos} profile={profile}/>
      </div>
    </div>
  )
}
